import React from "react";
import { Box, Typography, Stack, Chip } from "@mui/material";

interface TimerPreviewProps {
    previewDates: Date[];
    borderColor: string;
    secondaryColor: string;
    getTranslation: (key: string) => string;
    locale: string;
}

const TimerPreview: React.FC<TimerPreviewProps> = ({
    previewDates,
    borderColor,
    secondaryColor,
    getTranslation,
    locale,
}) => {
    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
            >
                <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.primary"
                    sx={{ letterSpacing: 1 }}
                >
                    {getTranslation("pm.timer_config.preview.title")}
                </Typography>
                <Chip
                    label={previewDates.length > 0 ? getTranslation("pm.timer_config.preview.valid") : getTranslation("pm.timer_config.preview.no_match")}
                    size="small"
                    sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        height: 24,
                        backgroundColor: previewDates.length > 0 ? "#dcfce7" : "#fee2e2",
                        color: previewDates.length > 0 ? "#15803d" : "#dc2626",
                    }}
                />
            </Stack>

            <Box
                className="timer-panel-scrollbar"
                sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}
            >
                {previewDates.length > 0 ? (
                    <Stack spacing={0.75}>
                        {previewDates.map((date, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    backgroundColor: "background.paper",
                                    p: 1,
                                    borderRadius: "8px",
                                    border: `1px solid ${borderColor}`,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "6px",
                                        backgroundColor: "action.hover",
                                        border: `1px solid ${borderColor}`,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "0.5rem",
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            color: "text.secondary",
                                            lineHeight: 1,
                                        }}
                                    >
                                        {date.toLocaleDateString(locale, {
                                            month: "short",
                                        })}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "0.85rem",
                                            fontWeight: 700,
                                            color: secondaryColor,
                                            lineHeight: 1,
                                        }}
                                    >
                                        {date.getDate()}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color={secondaryColor}
                                        sx={{
                                            textTransform: "capitalize",
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {date.toLocaleDateString(locale, {
                                            weekday: "long",
                                        })}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {date.toLocaleTimeString(locale, {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}{" "}
                                        • {date.getFullYear()}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {getTranslation("pm.timer_config.preview.no_dates")}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
                            {getTranslation("pm.timer_config.preview.relax_restrictions")}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default TimerPreview;
